import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Phone, ChevronRight, Star, CheckCircle, Shield, Award, Clock,
  ArrowRight, MapPin, Droplets, Wrench, Home, Building2,
  AlertTriangle, Search, Waves, ChevronDown, ChevronUp,
  BadgeCheck, ClipboardList, FileText, Hammer, Users, X,
} from 'lucide-react';
import { APP_CONFIG, ROUTES } from '../../config/app.config';
import { MOCK_TESTIMONIALS, MOCK_FAQS } from '../../mock';
import { MOCK_SERVICES } from '../../mock/services.mock';
import { ROOFING_SYSTEMS, STORM_CAPABILITIES, CLAIM_STEPS } from '../../mock/roofing-systems.mock';
import {
  PROOF_POINTS, COMPARISON_ROWS, INSPECTION_STEPS, PREMIUM_EXPLAINED,
  OWENS_CORNING_POINTS, CATES_DIFFERENCE, URGENCY_REASONS, SERVICE_AREAS_MAP,
  HEADLINE_STATS,
} from '../../mock/cates-content.mock';
import CountUp from '../../components/common/CountUp';
import GoogleReviewsCarousel from '../../components/common/GoogleReviewsCarousel';
import BeforeAfterSlider from '../../components/common/BeforeAfterSlider';
import ServiceAreaMap from '../../components/common/ServiceAreaMap';
import { useAppStore } from '../../store/useAppStore';
import { quoteNavState, quoteServiceForLabel } from '../../utils/quotePrefill';

const certifications = [
  { name: 'Owens Corning Preferred', sub: APP_CONFIG.certification.exclusivityShort, icon: '🏆' },
  { name: 'NRCA Member', sub: 'National Roofing Contractors', icon: '🏗️' },
  { name: 'Licensed, Bonded & Insured', sub: `FL Lic. #${APP_CONFIG.licenseNumber}`, icon: '🛡️' },
];

const proofIcons: Record<string, React.ComponentType<any>> = {
  star: Star, award: Award, users: Users, home: Home, file: FileText, shield: Shield,
};

const serviceIcons: Record<string, React.ComponentType<any>> = {
  'roof-replacement': Home,
  'insurance-claims': FileText,
  'roof-repair': Wrench,
  'storm-damage': AlertTriangle,
  'roof-cleaning': Droplets,
  'commercial': Building2,
  'emergency': AlertTriangle,
  'inspection': Search,
  'gutter-cleaning': Waves,
};

const BRAND_BLUE = '#0072c6';
const BRAND_LIGHT = '#1493e6';
const BRAND_SILVER = '#e6edf5';
const BRAND_DARK = '#050608';


const HomePage: React.FC = () => {
  const [activeFaq, setActiveFaq] = useState<string | null>(null);
  const navigate = useNavigate();
  const [heroForm, setHeroForm] = useState({ name: '', phone: '', email: '', address: '', service: '' });
  const updateHero = (k: string, v: string) => setHeroForm(f => ({ ...f, [k]: v }));
  const submitHeroForm = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(ROUTES.QUOTE, quoteNavState({
      name: heroForm.name.trim(),
      phone: heroForm.phone.trim(),
      email: heroForm.email.trim(),
      address: heroForm.address.trim(),
      service: quoteServiceForLabel(heroForm.service),
    }));
  };
  const { theme } = useAppStore();
  const isDark = theme === 'dark';
  const sectionBg = isDark ? BRAND_DARK : BRAND_SILVER;
  const altSectionBg = isDark ? BRAND_DARK : BRAND_SILVER;
  const headingColor = isDark ? 'white' : BRAND_DARK;
  const bodyColor = isDark ? BRAND_SILVER : BRAND_DARK;
  const softText = isDark ? 'rgba(255,255,255,0.72)' : 'rgba(5,6,8,0.72)';
  const heroTitleColor = 'white';
  const heroBodyColor = 'rgba(255,255,255,0.88)';
  const heroOverlay = isDark
    ? 'linear-gradient(90deg, rgba(5,6,8,0.48) 0%, rgba(5,6,8,0.32) 44%, rgba(0,114,198,0.26) 100%)'
    : 'linear-gradient(90deg, rgba(3,37,60,0.78) 0%, rgba(0,80,137,0.62) 48%, rgba(20,147,230,0.42) 100%)';
  const heroFade = isDark
    ? 'linear-gradient(180deg, rgba(5,6,8,0.02) 0%, rgba(5,6,8,0.32) 100%)'
    : 'linear-gradient(180deg, rgba(3,37,60,0.04) 0%, rgba(3,37,60,0.38) 100%)';

  return (
    <div>
      {/* ─── Hero — Video BG (Elo-style) ───────────────────────── */}
      <section style={{
        minHeight: 'clamp(660px, 84vh, 820px)', position: 'relative', display: 'flex', alignItems: 'center',
        background: sectionBg, overflow: 'hidden',
      }}>
        <img
          src={APP_CONFIG.heroImageUrl}
          alt=""
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: isDark ? 0.5 : 0.68 }}
        />
        {/* Video background — only rendered once Cates' own footage is configured */}
        {APP_CONFIG.heroVideoUrl && (
          <video
            autoPlay muted loop playsInline
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: isDark ? 0.38 : 0.46 }}
          >
            <source src={APP_CONFIG.heroVideoUrl} type="video/mp4" />
          </video>
        )}

        {/* Gradient overlay — navy to indigo */}
        <div style={{
          position: 'absolute', inset: 0,
          background: heroOverlay,
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          background: heroFade,
        }} />

        {/* Blue shimmer glow */}
        <div className="hero-glow" style={{
          position: 'absolute', top: '6rem', right: '7%',
          borderRadius: '50%', pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(20,147,230,0.42) 0%, rgba(0,114,198,0.22) 46%, transparent 72%)',
        }} />
        <div className="container-xl hero-inner" style={{ position: 'relative', zIndex: 1, width: '100%' }}>
          <div className="hero-grid" style={{ display: 'grid', gap: '3rem', alignItems: 'center' }}>
            <div>
              {/* Google reputation — the first thing a visitor sees */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.625rem', flexWrap: 'wrap',
                background: 'rgba(255,255,255,0.96)', border: '1px solid rgba(255,255,255,0.9)',
                borderRadius: '9999px', padding: '0.45rem 1.15rem', marginBottom: '1rem',
                boxShadow: '0 8px 24px rgba(5,8,15,0.22)',
              }}>
                <span style={{ display: 'flex', gap: '0.1rem' }}>
                  {Array.from({ length: APP_CONFIG.reputation.ratingStars }).map((_, i) => (
                    <Star key={i} size={15} fill="#f5b400" stroke="none" />
                  ))}
                </span>
                <span style={{ color: BRAND_DARK, fontSize: '0.82rem', fontWeight: 800 }}>
                  {APP_CONFIG.reputation.rating} on {APP_CONFIG.reputation.source}
                </span>
                <span style={{ color: 'rgba(5,6,8,0.62)', fontSize: '0.78rem', fontWeight: 600 }}>
                  {APP_CONFIG.reputation.reviewCount} reviews
                </span>
              </div>

              {/* Manufacturer credential */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                background: 'rgba(0,114,198,0.42)', border: '1px solid rgba(203,213,225,0.18)',
                borderRadius: '9999px', padding: '0.375rem 1.1rem', marginBottom: '1.5rem',
              }}>
                <Award size={14} color="white" />
                <span style={{ color: 'white', fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  {APP_CONFIG.certification.shortName} · {APP_CONFIG.ownership}
                </span>
              </div>

              <h1 style={{
                color: heroTitleColor, fontSize: 'clamp(2rem, 3.6vw, 3.3rem)',
                fontWeight: 900, lineHeight: 1.1, marginBottom: '1.25rem',
                textTransform: 'uppercase', letterSpacing: 0,
              }}>
                Jacksonville's Premium{' '}
                <span className="gradient-text">Roof Replacement</span>{' '}
                & Insurance Claim Specialists
              </h1>

              {/* Insurance claim proposition — above the fold, by design */}
              <div style={{
                background: 'rgba(5,6,8,0.42)', border: '1px solid rgba(255,255,255,0.22)',
                borderRadius: '0.9rem', padding: '1.1rem 1.25rem', marginBottom: '1.75rem',
                maxWidth: '520px', backdropFilter: 'blur(6px)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.45rem' }}>
                  <FileText size={16} color={BRAND_LIGHT} />
                  <span style={{ color: 'white', fontWeight: 800, fontSize: '0.86rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Insurance Claim Assistance
                  </span>
                </div>
                <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.93rem', lineHeight: 1.65, margin: 0 }}>
                  We inspect your roof, document storm damage, meet with your adjuster, and guide you
                  through the entire insurance process from inspection to completion.
                </p>
              </div>

              {/* CTA Buttons */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '2.5rem' }}>
                <Link to={ROUTES.QUOTE} className="btn-primary" style={{ fontSize: '0.95rem' }}>
                  Schedule Your Free Inspection <ArrowRight size={16} />
                </Link>
                <a href={`tel:${APP_CONFIG.phoneRaw}`} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.75rem 1.75rem', borderRadius: '100px', fontWeight: 700,
                  fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.04em',
                  border: '2px solid rgba(255,255,255,0.58)', color: 'white',
                  textDecoration: 'none', transition: 'all 0.25s ease',
                  fontFamily: "'Public Sans', Helvetica, Arial, Lucida, sans-serif",
                }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = BRAND_LIGHT; (e.currentTarget as HTMLElement).style.color = BRAND_LIGHT; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.58)'; (e.currentTarget as HTMLElement).style.color = 'white'; }}
                >
                  <Phone size={16} /> {APP_CONFIG.phone}
                </a>
              </div>

              {/* Trust Signals */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
                {[
                  { icon: <Search size={14} color={BRAND_LIGHT} />, text: 'Free Roof Inspections' },
                  { icon: <FileText size={14} color={BRAND_LIGHT} />, text: 'Insurance Claims Handled' },
                  { icon: <Shield size={14} color={BRAND_LIGHT} />, text: '10-Year Workmanship Warranty' },
                ].map(item => (
                  <div key={item.text} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    {item.icon}
                    <span style={{ color: heroBodyColor, fontSize: '0.85rem', fontWeight: 500 }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Quote Card */}
            <div className="hidden-mobile">
              <div style={{
                background: isDark ? 'rgba(12,20,29,0.94)' : 'rgba(238,245,251,0.92)', backdropFilter: 'blur(18px)',
                border: `1px solid ${isDark ? 'rgba(20,147,230,0.42)' : 'rgba(255,255,255,0.72)'}`, borderRadius: '1.25rem', padding: '2rem',
                boxShadow: '0 24px 64px rgba(5,6,8,0.36)',
              }}>
                {/* Gold top bar */}
                <div style={{ height: '4px', background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_LIGHT})`, borderRadius: '2px', marginBottom: '1.5rem' }} />
                <h3 style={{ color: isDark ? 'white' : BRAND_DARK, fontWeight: 800, marginBottom: '1.25rem', fontSize: '1.1rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Get Your Free Estimate
                </h3>
                <form onSubmit={submitHeroForm} style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                  <input className="input-field light-input" placeholder="Your Full Name" value={heroForm.name} onChange={e => updateHero('name', e.target.value)} style={{ background: 'white', borderColor: 'rgba(20,147,230,0.45)', color: BRAND_DARK }} />
                  <input className="input-field light-input" placeholder="Phone Number" value={heroForm.phone} onChange={e => updateHero('phone', e.target.value)} style={{ background: 'white', borderColor: 'rgba(20,147,230,0.45)', color: BRAND_DARK }} />
                  <input className="input-field light-input" type="email" placeholder="Email Address" value={heroForm.email} onChange={e => updateHero('email', e.target.value)} style={{ background: 'white', borderColor: 'rgba(20,147,230,0.45)', color: BRAND_DARK }} />
                  <input className="input-field light-input" placeholder="Property Address" value={heroForm.address} onChange={e => updateHero('address', e.target.value)} style={{ background: 'white', borderColor: 'rgba(20,147,230,0.45)', color: BRAND_DARK }} />
                  <select className="input-field light-input" value={heroForm.service} onChange={e => updateHero('service', e.target.value)} style={{ background: 'white', borderColor: 'rgba(20,147,230,0.45)', color: BRAND_DARK }}>
                    <option value="">Select Service...</option>
                    <option>Roof Replacement</option>
                    <option>Insurance Claim Assistance</option>
                    <option>Roof Repair</option>
                    <option>Free Roof Inspection</option>
                    <option>Commercial Roofing</option>
                    <option>Emergency Service</option>
                    <option>Roof Cleaning</option>
                  </select>
                  <button type="submit" className="btn-primary" style={{ justifyContent: 'center', textAlign: 'center', marginTop: '0.25rem' }}>
                    Request Free Estimate
                  </button>
                </form>
                <p style={{ color: BRAND_BLUE, fontSize: '0.75rem', textAlign: 'center', marginTop: '0.875rem' }}>
                  No obligation. Prefer not to call? Email us at{' '}
                  <a href={`mailto:${APP_CONFIG.email}`} style={{ color: BRAND_BLUE, fontWeight: 700 }}>{APP_CONFIG.email}</a>.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div style={{ position: 'absolute', bottom: '2rem', left: '50%', transform: 'translateX(-50%)', animation: 'bounce 2s infinite' }}>
          <ChevronDown size={24} color="rgba(255,255,255,0.4)" />
        </div>
        <style>{`
          @keyframes bounce { 0%,100%{transform:translateX(-50%) translateY(0)} 50%{transform:translateX(-50%) translateY(-8px)} }
          @media (max-width: 900px) { .hidden-mobile{display:none!important} }
        `}</style>
      </section>

      {/* ─── Proof bar — evidence, not adjectives ──────────────── */}
      <section style={{ background: altSectionBg, padding: '2.5rem 1.5rem' }}>
        <div className="container-xl">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem' }}>
            {PROOF_POINTS.map((p, i) => {
              const Icon = proofIcons[p.icon] || BadgeCheck;
              return (
                <div key={p.label} className={`reveal reveal-delay-${Math.min(i + 1, 5)}`}
                  style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <div style={{
                    width: '2.5rem', height: '2.5rem', borderRadius: '0.7rem', background: BRAND_BLUE,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Icon size={19} color="white" strokeWidth={2.4} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 900, color: headingColor, fontSize: '1rem', lineHeight: 1.2 }}>
                      {'animate' in p && p.animate ? <CountUp value={p.value} /> : p.value}
                    </div>
                    <div style={{ color: BRAND_LIGHT, fontSize: '0.76rem', fontWeight: 600, lineHeight: 1.35 }}>{p.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Headline stats — animated, blended from the v1 site ─── */}
      {/* Solid brand blue, not the gradient used below: the insurance section
          immediately follows with a gradient, and two adjacent gradient bands
          bleed into one another. Solid also matches v1's blue stat treatment. */}
      <section style={{ background: BRAND_BLUE, padding: '3.25rem 1.5rem' }}>
        <div className="container-xl">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '2rem', textAlign: 'center' }}>
            {HEADLINE_STATS.map((stat, i) => (
              <div key={stat.label} className={`reveal reveal-delay-${i + 1}`}>
                <div style={{ fontSize: '1.6rem', marginBottom: '0.35rem', lineHeight: 1 }}>{stat.icon}</div>
                <div style={{
                  color: 'white', fontWeight: 900, lineHeight: 1,
                  fontSize: 'clamp(2rem, 3.6vw, 2.9rem)',
                  textShadow: '0 2px 18px rgba(5,8,15,0.28)',
                }}>
                  <CountUp value={stat.value} />
                </div>
                <div style={{
                  color: 'rgba(255,255,255,0.88)', fontSize: '0.8rem', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: '0.5rem',
                }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Insurance Claims — primary differentiator ──────────── */}
      <section className="section-padding" style={{ background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND_BLUE} 62%, ${BRAND_LIGHT} 100%)` }}>
        <div className="container-xl">
          <div style={{ display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', gap: '3rem', alignItems: 'center' }}>
            <div className="reveal-left">
              <span style={{ color: 'white', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.85 }}>
                WE SPECIALIZE IN INSURANCE CLAIMS
              </span>
              <h2 style={{ fontSize: 'clamp(1.85rem, 3.2vw, 2.6rem)', fontWeight: 900, marginTop: '0.5rem', marginBottom: '1rem', color: 'white', lineHeight: 1.12 }}>
                Insurance Claim Assistance,<br />Start to Finish
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.92)', fontSize: '1.02rem', lineHeight: 1.75, marginBottom: '1.5rem', maxWidth: '520px' }}>
                Roof damage is stressful enough. Dealing with your insurance company shouldn’t make it worse. We guide you through the entire claim, from inspection and documentation to the adjuster meeting and paperwork, helping make sure you get the coverage your policy provides.
              </p>
              <p style={{ color: 'rgba(255,255,255,0.92)', fontSize: '1.02rem', lineHeight: 1.75, marginBottom: '2rem', maxWidth: '520px' }}>
                One point of contact. No chasing claim numbers or adjusters while you’re trying to work.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                <Link to="/services/insurance-claims" style={{
                  background: 'white', color: BRAND_DARK, padding: '0.8rem 2rem', borderRadius: '100px',
                  fontWeight: 800, textDecoration: 'none', fontSize: '0.9rem', textTransform: 'uppercase',
                  letterSpacing: '0.04em', display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                }}>
                  How Our Claim Process Works <ArrowRight size={16} />
                </Link>
                <Link to={ROUTES.QUOTE} style={{
                  border: '2px solid rgba(255,255,255,0.9)', color: 'white', padding: '0.8rem 2rem',
                  borderRadius: '100px', fontWeight: 700, textDecoration: 'none', fontSize: '0.9rem',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                  Free Roof Inspection
                </Link>
              </div>
            </div>

            <div className="reveal-right" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {CLAIM_STEPS.map(step => (
                <div key={step.step} style={{
                  display: 'flex', gap: '1rem', alignItems: 'flex-start',
                  background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.22)',
                  borderRadius: '0.875rem', padding: '1rem 1.15rem', backdropFilter: 'blur(8px)',
                }}>
                  <span style={{ color: 'white', fontWeight: 900, fontSize: '1.05rem', opacity: 0.7, flexShrink: 0 }}>{step.step}</span>
                  <div>
                    <div style={{ color: 'white', fontWeight: 800, fontSize: '0.92rem', marginBottom: '0.2rem' }}>{step.title}</div>
                    <div style={{ color: 'rgba(255,255,255,0.86)', fontSize: '0.84rem', lineHeight: 1.6 }}>{step.text}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Services ──────────────────────────────────────────── */}
      <section className="section-padding" style={{ background: altSectionBg }}>
        <div className="container-xl">
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }} className="reveal">
            <span style={{ color: BRAND_LIGHT, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>What We Do</span>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', color: headingColor, textTransform: 'uppercase' }}>
              Complete Roofing Solutions
            </h2>
            <p style={{ color: bodyColor, fontSize: '1rem', maxWidth: '540px', margin: '0.75rem auto 0' }}>
              From roof repairs to full commercial installations, we handle every roofing project with quality workmanship from start to finish.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {MOCK_SERVICES.map((service, i) => {
              const IconComp = serviceIcons[service.category] || Home;
              return (
                <Link key={service.id} to={`/services/${service.slug}`} style={{ textDecoration: 'none' }}
                  className={`reveal-scale reveal-delay-${Math.min(i + 1, 5)}`}>
                  <div className="card card-hover" style={{ height: '100%', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                      <div style={{
                        width: '3.5rem', height: '3.5rem', borderRadius: '0.875rem',
                        background: BRAND_BLUE,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 12px 28px rgba(0,114,198,0.22)',
                      }}>
                        <IconComp size={26} color="white" strokeWidth={2.5} />
                      </div>

                      {service.popular && (
                        <span
                          className="badge badge-accent"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                        >
                          <CheckCircle size={13} strokeWidth={2.5} />
                          Most Popular
                          <CheckCircle size={13} strokeWidth={2.5} />
                        </span>
                      )}
                    </div>
                    <h3 style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.5rem', color: headingColor }}>{service.title}</h3>
                    <p style={{ color: softText, fontSize: '0.875rem', lineHeight: 1.6, marginBottom: '1.25rem' }}>{service.shortDesc}</p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: BRAND_BLUE, fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Learn More <ChevronRight size={14} />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Roofing Systems We Install ────────────────────────── */}
      <section className="section-padding" style={{ background: sectionBg }}>
        <div className="container-xl">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }} className="reveal">
            <span style={{ color: BRAND_LIGHT, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Materials</span>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', color: headingColor, textTransform: 'uppercase' }}>
              Roofing Systems We Install
            </h2>
            <p style={{ color: bodyColor, fontSize: '1rem', maxWidth: '600px', margin: '0.75rem auto 0' }}>
              From architectural asphalt to slate, we install every roof as a complete system. We’ll help you choose what actually makes sense for your home, your climate, and your budget.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {ROOFING_SYSTEMS.map((sys, i) => (
              <div key={sys.id} className={`reveal-scale reveal-delay-${Math.min(i + 1, 5)}`}>
                <div className="card card-hover" style={{ height: '100%', padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ position: 'relative', height: '190px', flexShrink: 0 }}>
                    {sys.image ? (
                      <img
                        src={sys.image}
                        alt={sys.name}
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    ) : (
                      <div style={{
                        width: '100%', height: '100%',
                        background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND_BLUE} 100%)`,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                      }}>
                        <Home size={30} color="rgba(255,255,255,0.85)" strokeWidth={1.6} />
                        <span style={{ color: 'rgba(255,255,255,0.92)', fontWeight: 800, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          {sys.name}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.62)', fontSize: '0.7rem', letterSpacing: '0.04em' }}>
                          Photography coming soon
                        </span>
                      </div>
                    )}
                    {sys.featured && (
                      <span style={{
                        position: 'absolute', top: '0.75rem', left: '0.75rem',
                        background: BRAND_BLUE, color: 'white', fontSize: '0.68rem', fontWeight: 800,
                        letterSpacing: '0.06em', textTransform: 'uppercase',
                        padding: '0.3rem 0.7rem', borderRadius: '9999px',
                      }}>Most Installed</span>
                    )}
                  </div>
                  <div style={{ padding: '1.35rem', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <h3 style={{ fontWeight: 800, fontSize: '1.08rem', marginBottom: '0.4rem', color: headingColor }}>{sys.name}</h3>
                    <p style={{ color: BRAND_LIGHT, fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.6rem', lineHeight: 1.5 }}>{sys.blurb}</p>
                    <p style={{ color: softText, fontSize: '0.85rem', lineHeight: 1.65, marginBottom: '1rem' }}>{sys.detail}</p>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 'auto 0 0', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      {sys.highlights.map(h => (
                        <li key={h} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: softText, fontSize: '0.82rem' }}>
                          <CheckCircle size={14} color={BRAND_LIGHT} style={{ flexShrink: 0 }} /> {h}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to={ROUTES.QUOTE} className="btn-primary" style={{ fontSize: '0.9rem' }}>
              Get a Free Estimate <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Storm Damage Response ─────────────────────────────── */}
      <section className="section-padding" style={{ background: altSectionBg }}>
        <div className="container-xl">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', alignItems: 'center' }}>
            <div className="reveal-left">
              <span style={{ color: BRAND_LIGHT, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Storm Response</span>
              <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', marginBottom: '1rem', color: headingColor, textTransform: 'uppercase', lineHeight: 1.15 }}>
                Storm Damage? Don't Wait.
              </h2>
              <p style={{ color: softText, lineHeight: 1.75, marginBottom: '1.25rem', fontSize: '1rem' }}>
                High winds and hail can leave behind roof damage that’s easy to miss from the ground, including lifted or missing shingles, damaged flashing, granule loss, and leaks that may not show up until later.
              </p>
              <p style={{ color: softText, lineHeight: 1.75, marginBottom: '1.75rem', fontSize: '1rem' }}>
                We respond quickly, tarp what needs protecting, inspect and document the damage, and help you determine the right next step.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '2rem' }}>
                {STORM_CAPABILITIES.map(c => (
                  <div key={c} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: bodyColor, fontSize: '0.88rem', fontWeight: 600 }}>
                    <CheckCircle size={15} color={BRAND_LIGHT} style={{ flexShrink: 0 }} /> {c}
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                <Link to={ROUTES.QUOTE} className="btn-primary" style={{ fontSize: '0.9rem' }}>
                  Schedule a Storm Damage Inspection <ArrowRight size={16} />
                </Link>
                <a href={`tel:${APP_CONFIG.phoneRaw}`} className="btn-secondary" style={{ fontSize: '0.9rem' }}>
                  <Phone size={16} /> {APP_CONFIG.phone}
                </a>
              </div>
            </div>

            <div className="reveal-right">
              <div style={{
                background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND_BLUE} 100%)`,
                borderRadius: '1.25rem', padding: '2.5rem 2rem',
                boxShadow: '0 24px 60px rgba(5,8,15,0.28)', color: 'white',
              }}>
                <AlertTriangle size={34} color="white" strokeWidth={2} />
                <h3 style={{ fontWeight: 900, fontSize: '1.35rem', margin: '1rem 0 0.75rem', textTransform: 'uppercase', lineHeight: 1.25 }}>
                  Damage Doesn't Wait for Your Insurer
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '1.75rem' }}>
                  Most insurance policies require homeowners to take reasonable steps to prevent further damage after a loss. We tarp what needs protecting and document the damage as we go, so you’re protecting your home while preserving a clear record for your claim.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
                  {[
                    { k: 'Emergency response', v: ' Tarping to prevent further damage' },
                    { k: 'Free inspection', v: ' Honest answer on whether to file' },
                    { k: 'Full documentation', v: 'Detailed photos and measurements for your claim' },
                  ].map(row => (
                    <div key={row.k} style={{ borderTop: '1px solid rgba(255,255,255,0.25)', paddingTop: '0.9rem' }}>
                      <div style={{ fontWeight: 800, fontSize: '0.92rem' }}>{row.k}</div>
                      <div style={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.85rem', marginTop: '0.15rem' }}>{row.v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Why Choose Cates? — head-to-head comparison ────────── */}
      <section className="section-padding" style={{ background: altSectionBg }}>
        <div className="container-xl">
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }} className="reveal">
            <span style={{ color: BRAND_LIGHT, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>The Difference</span>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', color: headingColor, textTransform: 'uppercase' }}>
              Why Choose Cates Roofing?
            </h2>
          </div>

          <div style={{ maxWidth: '820px', margin: '0 auto', borderRadius: '1rem', overflow: 'hidden', border: `1px solid ${isDark ? 'rgba(20,147,230,0.35)' : 'rgba(0,114,198,0.22)'}` }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
              <div style={{ background: BRAND_BLUE, color: 'white', padding: '1rem 1.25rem', fontWeight: 900, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Cates Roofing
              </div>
              <div style={{ background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(5,6,8,0.72)', color: 'white', padding: '1rem 1.25rem', fontWeight: 800, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Typical Roofing Company
              </div>
            </div>
            {COMPARISON_ROWS.map((row, i) => (
              <div key={row.cates} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '1rem 1.25rem',
                  background: isDark ? (i % 2 ? 'rgba(0,114,198,0.14)' : 'rgba(0,114,198,0.22)') : (i % 2 ? '#ffffff' : '#f4f8fc'),
                  color: headingColor, fontSize: '0.9rem', fontWeight: 700,
                }}>
                  <CheckCircle size={17} color={BRAND_LIGHT} style={{ flexShrink: 0 }} /> {row.cates}
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '1rem 1.25rem',
                  background: isDark ? (i % 2 ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.06)') : (i % 2 ? '#f7f7f8' : '#eeeff1'),
                  color: softText, fontSize: '0.9rem',
                }}>
                  <X size={16} color="#b3241f" style={{ flexShrink: 0 }} /> {row.typical}
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '2.25rem' }}>
            <Link to={ROUTES.QUOTE} className="btn-primary" style={{ fontSize: '0.9rem' }}>
              Get Your Free Roof Evaluation <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Free inspection as a process ──────────────────────── */}
      <section className="section-padding" style={{ background: sectionBg }}>
        <div className="container-xl">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }} className="reveal">
            <span style={{ color: BRAND_LIGHT, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>No Cost, No Obligation</span>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', color: headingColor, textTransform: 'uppercase' }}>
              Our Free Roof Inspection Includes
            </h2>
            <p style={{ color: bodyColor, fontSize: '1rem', maxWidth: '600px', margin: '0.75rem auto 0' }}>
              Not a five-minute look from the driveway. Here is exactly what we do, and what you
              receive at the end of it — whether or not you hire us.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
            {INSPECTION_STEPS.map((step, i) => (
              <div key={step.title} className={`reveal-scale reveal-delay-${Math.min(i + 1, 5)}`}>
                <div className="card" style={{ height: '100%', display: 'flex', gap: '0.9rem' }}>
                  <CheckCircle size={22} color={BRAND_LIGHT} style={{ flexShrink: 0, marginTop: '0.15rem' }} />
                  <div>
                    <h3 style={{ fontWeight: 800, fontSize: '0.98rem', marginBottom: '0.35rem', color: headingColor }}>{step.title}</h3>
                    <p style={{ color: softText, fontSize: '0.86rem', lineHeight: 1.65, margin: 0 }}>{step.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to={ROUTES.QUOTE} className="btn-primary" style={{ fontSize: '0.9rem' }}>
              Schedule Your Free Inspection <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Owens Corning authority ───────────────────────────── */}
      <section className="section-padding" style={{ background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND_BLUE} 100%)` }}>
        <div className="container-xl">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }} className="reveal">
            <span style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Manufacturer Credential</span>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, marginTop: '0.5rem', color: 'white', textTransform: 'uppercase' }}>
              Owens Corning Preferred Contractor
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.02rem', maxWidth: '640px', margin: '0.75rem auto 0', lineHeight: 1.7 }}>
              Owens Corning Preferred Contractors meet specific requirements set by one of the most recognized names in roofing. For you, that means a contractor committed to proper installation, quality materials, and manufacturer standards from the roof deck up.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {OWENS_CORNING_POINTS.map((point, i) => (
              <div key={point.title} className={`reveal-scale reveal-delay-${i + 1}`}
                style={{
                  background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.24)',
                  borderRadius: '1rem', padding: '1.75rem', backdropFilter: 'blur(8px)',
                }}>
                <Award size={26} color="white" strokeWidth={2.2} />
                <h3 style={{ color: 'white', fontWeight: 800, fontSize: '1.05rem', margin: '0.9rem 0 0.6rem' }}>{point.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.88)', fontSize: '0.9rem', lineHeight: 1.7, margin: 0 }}>{point.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── What "premium" actually means ─────────────────────── */}
      <section className="section-padding" style={{ background: altSectionBg }}>
        <div className="container-xl">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }} className="reveal">
            <span style={{ color: BRAND_LIGHT, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Specifics, Not Slogans</span>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', color: headingColor, textTransform: 'uppercase' }}>
              What "Premium Roofing" Actually Means
            </h2>
            <p style={{ color: bodyColor, fontSize: '1rem', maxWidth: '620px', margin: '0.75rem auto 0' }}>
              Every roofer says “premium.” Here’s what it means on a Cates roof, component by component.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.25rem' }}>
            {PREMIUM_EXPLAINED.map((item, i) => (
              <div key={item.title} className={`reveal-scale reveal-delay-${Math.min(i + 1, 5)}`}>
                <div className="card" style={{ height: '100%' }}>
                  <div style={{
                    width: '2.6rem', height: '2.6rem', borderRadius: '0.7rem', background: BRAND_BLUE,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.9rem',
                  }}>
                    <Hammer size={20} color="white" strokeWidth={2.4} />
                  </div>
                  <h3 style={{ fontWeight: 800, fontSize: '1rem', marginBottom: '0.45rem', color: headingColor }}>{item.title}</h3>
                  <p style={{ color: softText, fontSize: '0.875rem', lineHeight: 1.68, margin: 0 }}>{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Why Choose Us ─────────────────────────────────────── */}
      <section className="section-padding" style={{ background: altSectionBg }}>
        <div className="container-xl">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
            <div className="reveal-left">
              <span style={{ color: BRAND_LIGHT, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Why Cates Roofing</span>
              <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', marginBottom: '1.25rem', color: headingColor, textTransform: 'uppercase' }}>
                The Roofing Company That Treats You Like Family
              </h2>
              <p style={{ color: softText, lineHeight: 1.7, marginBottom: '2rem', fontSize: '1rem' }}>
                We’re locally and family owned, with four generations in construction. What started as a family tradition of skilled workmanship has grown into Cates Roofing, built on the same principles: quality materials, reliable crews, and standing behind the work we do.
              </p>
              {[
                { icon: <Shield size={20} color="white" />, title: '10-Year Workmanship Warranty', desc: 'We stand behind our workmanship long after the job is finished.' },
                { icon: <Award size={20} color="white" />, title: 'Owens Corning Preferred Contractor', desc: 'Manufacturer credentialing that reflects our commitment to proper installation and established roofing standards.' },
                { icon: <CheckCircle size={20} color="white" />, title: 'Experienced, Reliable Crews', desc: 'Consistent crews, clear accountability, and workmanship you can depend on.' },
                { icon: <Clock size={20} color="white" />, title: 'On-Time, Every Time', desc: 'We respect your schedule and keep you informed from start to finish.' },
              ].map(item => (
                <div key={item.title} style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem' }}>
                  <div style={{
                    width: '2.5rem', height: '2.5rem', borderRadius: '0.625rem',
                    background: 'rgba(0,114,198,0.55)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {item.icon}
                  </div>
                  <div>
                    <h4 style={{ fontWeight: 700, color: headingColor, marginBottom: '0.25rem', fontSize: '0.95rem' }}>{item.title}</h4>
                    <p style={{ color: softText, fontSize: '0.875rem', margin: 0, lineHeight: 1.6 }}>{item.desc}</p>
                  </div>
                </div>
              ))}
              <Link to={ROUTES.ABOUT} className="btn-primary" style={{ marginTop: '0.5rem' }}>
                Learn Our Story <ArrowRight size={16} />
              </Link>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }} className="reveal-right">
              <img src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=500&auto=format&fit=crop&q=80" alt="Roof repair" style={{ borderRadius: '1rem', objectFit: 'cover', height: '240px', width: '100%' }} onError={e => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=500'; }} />
              <img src="https://images.unsplash.com/photo-1613977257363-707ba9348227?w=500&auto=format&fit=crop&q=80" alt="New roof" style={{ borderRadius: '1rem', objectFit: 'cover', height: '240px', width: '100%', marginTop: '2rem' }} onError={e => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=500'; }} />
              <img src="https://images.unsplash.com/photo-1486325212027-8081e485255e?w=500&auto=format&fit=crop&q=80" alt="Commercial" style={{ borderRadius: '1rem', objectFit: 'cover', height: '240px', width: '100%', marginTop: '-2rem' }} onError={e => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=500'; }} />
              <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=500&auto=format&fit=crop&q=80" alt="Cleaning" style={{ borderRadius: '1rem', objectFit: 'cover', height: '240px', width: '100%' }} onError={e => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=500'; }} />
            </div>
          </div>
        </div>
        <style>{`@media(max-width:900px){ div[style*="grid-template-columns: 1fr 1fr"]{grid-template-columns:1fr!important} }`}</style>
      </section>

      {/* ─── Certifications ─────────────────────────────────────── */}
      <section style={{ background: sectionBg, padding: '3.5rem 1.5rem' }}>
        <div className="container-xl">
          <p style={{ textAlign: 'center', color: BRAND_LIGHT, fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '2rem' }} className="reveal">
            Certifications & Memberships
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            {certifications.map((cert, i) => (
              <div key={cert.name}
                className={`reveal-scale reveal-delay-${i + 1}`}
                style={{
                  textAlign: 'center', padding: '1.75rem 1rem',
                  background: isDark ? 'rgba(5,8,15,0.98)' : '#ffffff',
                  border: `1px solid ${isDark ? 'rgba(20,147,230,0.42)' : 'rgba(0,114,198,0.28)'}`,
                  borderRadius: '1rem',
                  boxShadow: isDark ? 'none' : '0 8px 24px rgba(5,6,8,0.08)',
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px) scale(1.03)'; (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 32px rgba(0,114,198,0.28)`; }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.transform = '';
                  (e.currentTarget as HTMLElement).style.boxShadow = isDark ? 'none' : '0 8px 24px rgba(5,6,8,0.08)';
                }}
              >
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{cert.icon}</div>
                <div style={{ color: headingColor, fontWeight: 700, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{cert.name}</div>
                <div style={{ color: BRAND_LIGHT, fontSize: '0.75rem', marginTop: '0.25rem' }}>{cert.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Before/After Gallery ──────────────────────────────── */}
      <section className="section-padding" style={{ background: altSectionBg }}>
        <div className="container-xl">
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }} className="reveal">
            <span style={{ color: BRAND_LIGHT, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Our Work</span>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', color: headingColor, textTransform: 'uppercase' }}>
              Before & After Transformations
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '2rem' }}>
            {[
              { before: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800', after: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800', title: 'Full Replacement — Jacksonville', caption: 'Originally approved for repairs. After additional inspection and documentation, the roof was reassessed for full replacement.' },
              { before: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800', after: 'https://images.unsplash.com/photo-1613977257363-707ba9348227?w=800', title: 'Storm Damage — Orange Park', caption: 'Wind damage and displaced flashing identified and documented. We protected the roof from further damage and completed the replacement.' },
              { before: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800', after: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800', title: 'Soft-Wash Clean — Ponte Vedra', caption: 'Algae and black streaks removed with a soft wash treatment designed to clean the roof without damaging the shingles.' },
            ].map((item, i) => (
              <div key={item.title} className={`reveal-scale reveal-delay-${i + 1}`}>
                <BeforeAfterSlider before={item.before} after={item.after} title={item.title} caption={item.caption} />
              </div>
            ))}
          </div>

          <p style={{ textAlign: 'center', color: softText, fontSize: '0.8rem', marginTop: '1.5rem', maxWidth: '620px', marginLeft: 'auto', marginRight: 'auto' }}>
            Placeholder imagery — to be replaced with Cates' own before-and-after job photography.
          </p>

          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to={ROUTES.GALLERY} className="btn-secondary">View Full Gallery <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* ─── Testimonials ──────────────────────────────────────── */}
      <section className="section-padding" style={{ background: sectionBg }}>
        <div className="container-xl">
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }} className="reveal">
            <span style={{ color: BRAND_LIGHT, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Customer Reviews</span>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', color: headingColor, textTransform: 'uppercase' }}>
              What Our Customers Say
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem', marginTop: '0.75rem' }}>
              {[1, 2, 3, 4, 5].map(i => <Star key={i} size={22} fill="#fbbc04" stroke="none" />)}
              <span style={{ fontWeight: 800, color: headingColor, marginLeft: '0.5rem', fontSize: '1.1rem' }}>{APP_CONFIG.reputation.rating}</span>
              <span style={{ color: softText, fontSize: '0.9rem' }}>· {APP_CONFIG.reputation.reviewCount} verified {APP_CONFIG.reputation.source} reviews</span>
            </div>
          </div>
          <GoogleReviewsCarousel />

          <p style={{
            textAlign: 'center', color: softText, fontSize: '0.82rem', marginTop: '0.5rem',
            maxWidth: '640px', marginLeft: 'auto', marginRight: 'auto',
          }}>
            Placeholder review content, shown to demonstrate the layout. To be replaced with real
            Google reviews before launch — at which point the Google mark and verified tick switch on.
          </p>

          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Link to={ROUTES.TESTIMONIALS} className="btn-secondary" style={{ fontSize: '0.9rem' }}>
              Read More Customer Stories <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Process ───────────────────────────────────────────── */}
      <section className="section-padding" style={{ background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND_BLUE} 58%, ${BRAND_LIGHT} 100%)` }}>
        <div className="container-xl">
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }} className="reveal">
            <span style={{ color: 'rgba(255,255,255,0.76)', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Simple Process</span>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', color: 'white', textTransform: 'uppercase' }}>
              How It Works
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '2rem' }}>
            {[
              { step: '01', title: 'Request a Quote', desc: 'Fill out our quick online form or give us a call. We’ll get back to you promptly to schedule your inspection.', icon: '📋' },
              { step: '02', title: 'Free Inspection', desc: 'We inspect your roof, document our findings, and give you a clear assessment of its condition.', icon: '🔍' },
              { step: '03', title: 'Review Proposal', desc: 'We walk you through our findings and provide a clear, itemized proposal. No surprise fees. No pressure.', icon: '📄' },
              { step: '04', title: 'We Get to Work', desc: 'Our experienced crew completes your project with quality materials, careful workmanship, and respect for your property.', icon: '🏗️' },
              { step: '05', title: 'Final Walkthrough', desc: 'We inspect the completed work with you, answer any questions, and make sure everything is right before we consider the job complete.', icon: '✅' },
            ].map((step, i) => {
              const StepIcon = [ClipboardList, Search, FileText, Hammer, BadgeCheck][i];
              return (
                <div key={step.step} style={{ textAlign: 'center' }} className={`reveal-scale reveal-delay-${i + 1}`}>
                  <div style={{
                    width: '4.5rem', height: '4.5rem', borderRadius: '50%',
                    background: 'rgba(255,255,255,0.14)',
                    border: '1px solid rgba(255,255,255,0.34)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 1.25rem',
                    boxShadow: '0 8px 24px rgba(5,6,8,0.2)',
                  }}>
                    <StepIcon size={30} color="white" strokeWidth={2} />
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.72)', fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                    Step {step.step}
                  </div>
                  <h3 style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.5rem', color: 'white' }}>{step.title}</h3>
                  <p style={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.875rem', lineHeight: 1.6 }}>{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── FAQ ───────────────────────────────────────────────── */}
      <section className="section-padding" style={{ background: sectionBg }}>
        <div className="container-xl" style={{ maxWidth: '720px' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }} className="reveal">
            <span style={{ color: BRAND_LIGHT, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>FAQ</span>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', color: headingColor, textTransform: 'uppercase' }}>
              Common Questions
            </h2>
          </div>
          {MOCK_FAQS.slice(0, 5).map((faq, i) => (
            <div key={faq.id} className={`card reveal reveal-delay-${Math.min(i + 1, 5)}`} style={{ marginBottom: '0.875rem', padding: '0', borderRadius: '1.25rem', overflow: 'hidden' }}>
              <button
                onClick={() => setActiveFaq(activeFaq === faq.id ? null : faq.id)}
                style={{
                  width: '100%', padding: '1.25rem 1.5rem',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: activeFaq === faq.id ? `rgba(0,114,198,0.28)` : 'none',
                  border: 'none', cursor: 'pointer', textAlign: 'left', gap: '1rem',
                  transition: 'background 0.2s ease',
                }}>
                <span style={{ fontWeight: 600, fontSize: '0.95rem', color: headingColor }}>{faq.question}</span>
                {activeFaq === faq.id
                  ? <ChevronUp size={18} style={{ color: BRAND_LIGHT, flexShrink: 0 }} />
                  : <ChevronDown size={18} style={{ color: bodyColor, flexShrink: 0 }} />}
              </button>
              {activeFaq === faq.id && (
                <div style={{ padding: '0 1.5rem 1.25rem' }} className="fade-in">
                  <p style={{ color: bodyColor, fontSize: '0.9rem', lineHeight: 1.7, margin: 0 }}>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Link to={ROUTES.FAQ} className="btn-secondary">View All FAQs <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* ─── Service area map ──────────────────────────────────── */}
      <section className="section-padding" style={{ background: sectionBg }}>
        <div className="container-xl">
          <div style={{ display: 'grid', gridTemplateColumns: '0.85fr 1.15fr', gap: '3rem', alignItems: 'center' }}>
            <div className="reveal-left">
              <span style={{ color: BRAND_LIGHT, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Where We Work</span>
              <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', marginBottom: '1rem', color: headingColor, textTransform: 'uppercase', lineHeight: 1.15 }}>
                Serving All of {APP_CONFIG.region}
              </h2>
              <p style={{ color: softText, lineHeight: 1.75, marginBottom: '1.5rem', fontSize: '1rem' }}>
                From Fernandina Beach to St. Augustine and inland through Orange Park and Middleburg, our crews serve homeowners throughout Northeast Florida. We’re local, so when a storm comes through, we’re already here. 
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.75rem' }}>
                {SERVICE_AREAS_MAP.map(a => (
                  <span key={a.city} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                    border: `1px solid rgba(0,114,198,0.34)`, borderRadius: '9999px',
                    padding: '0.3rem 0.75rem', fontSize: '0.78rem', color: bodyColor, fontWeight: 600,
                  }}>
                    <MapPin size={11} color={BRAND_LIGHT} /> {a.city}
                  </span>
                ))}
              </div>
              <Link to={ROUTES.QUOTE} className="btn-primary" style={{ fontSize: '0.9rem' }}>
                Request a Roof Replacement Quote <ArrowRight size={16} />
              </Link>
            </div>
            <div className="reveal-right">
              <ServiceAreaMap />
            </div>
          </div>
        </div>
      </section>

      {/* ─── Real urgency ──────────────────────────────────────── */}
      <section className="section-padding" style={{ background: altSectionBg }}>
        <div className="container-xl">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }} className="reveal">
            <span style={{ color: BRAND_LIGHT, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Why Timing Matters</span>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', color: headingColor, textTransform: 'uppercase' }}>
              Roofs Do Not Get Cheaper By Waiting
            </h2>
            <p style={{ color: bodyColor, fontSize: '1rem', maxWidth: '620px', margin: '0.75rem auto 0' }}>
             No scare tactics or invented deadlines. These are the real reasons addressing roof damage early can save Florida homeowners money.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
            {URGENCY_REASONS.map((r, i) => (
              <div key={r.title} className={`reveal-scale reveal-delay-${Math.min(i + 1, 5)}`}>
                <div className="card" style={{ height: '100%', borderLeft: `3px solid ${BRAND_BLUE}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginBottom: '0.5rem' }}>
                    <AlertTriangle size={17} color={BRAND_LIGHT} />
                    <h3 style={{ fontWeight: 800, fontSize: '0.98rem', color: headingColor, margin: 0 }}>{r.title}</h3>
                  </div>
                  <p style={{ color: softText, fontSize: '0.86rem', lineHeight: 1.65, margin: 0 }}>{r.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to={ROUTES.QUOTE} className="btn-primary" style={{ fontSize: '0.9rem' }}>
              Start Your Insurance Claim <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── The Cates Difference ──────────────────────────────── */}
      <section className="section-padding" style={{ background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND_BLUE} 100%)` }}>
        <div className="container-xl">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }} className="reveal">
            <span style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>How We Run a Job</span>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, marginTop: '0.5rem', color: 'white', textTransform: 'uppercase' }}>
              The Cates Difference
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.15rem' }}>
            {CATES_DIFFERENCE.map((item, i) => (
              <div key={item.title} className={`reveal-scale reveal-delay-${Math.min(i + 1, 5)}`}
                style={{
                  background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.22)',
                  borderRadius: '0.9rem', padding: '1.35rem',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <BadgeCheck size={18} color="white" strokeWidth={2.4} />
                  <h3 style={{ color: 'white', fontWeight: 800, fontSize: '0.95rem', margin: 0 }}>{item.title}</h3>
                </div>
                <p style={{ color: 'rgba(255,255,255,0.86)', fontSize: '0.85rem', lineHeight: 1.65, margin: 0 }}>{item.text}</p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to={ROUTES.QUOTE} style={{
              background: 'white', color: BRAND_DARK, padding: '0.8rem 2rem', borderRadius: '100px',
              fontWeight: 800, textDecoration: 'none', fontSize: '0.9rem', textTransform: 'uppercase',
              letterSpacing: '0.04em', display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            }}>
              Schedule Your Free Inspection <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Emergency CTA ──────────────────────────────────────── */}
      <section style={{ background: sectionBg, padding: '4.5rem 1.5rem' }}>
        <div className="container-xl" style={{ textAlign: 'center' }}>
          <div style={{
            background: `linear-gradient(135deg, rgba(0,114,198,0.75), rgba(0,114,198,0.45))`,
            border: '1px solid rgba(0,114,198,0.75)', borderRadius: '1.5rem',
            padding: '3.5rem 2rem', maxWidth: '660px', margin: '0 auto',
          }} className="reveal-scale">
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🚨</div>
            <h2 style={{ color: 'white', fontWeight: 900, fontSize: '1.75rem', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: 0 }}>
              Roof Emergency?
            </h2>
            <p style={{ color: 'white', marginBottom: '2rem', fontSize: '1rem', maxWidth: '480px', margin: '0 auto 2rem' }}>
              We’re available 24 hours a day, 7 days a week for urgent roof damage. We respond quickly to assess the problem, protect your home, and help prevent further damage.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
              <a href={`tel:${APP_CONFIG.phoneRaw}`} className="btn-primary" style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, ${BRAND_LIGHT})`, boxShadow: '0 4px 20px rgba(0,114,198,0.25)' }}>
                <Phone size={16} /> Call Emergency Line
              </a>
              <Link to="/services/emergency-roofing" style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.75rem 1.75rem', borderRadius: '100px', fontWeight: 700,
                fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.04em',
                border: '2px solid rgba(255,255,255,0.85)', color: 'white',
                textDecoration: 'none', transition: 'all 0.25s ease',
                fontFamily: "'Public Sans', Helvetica, Arial, Lucida, sans-serif",
              }}>
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Financing ──────────────────────────────────────────── */}
      <section className="section-padding" style={{ background: sectionBg }}>
        <div className="container-xl" style={{ textAlign: 'center', maxWidth: '700px' }}>
          <span style={{ color: BRAND_LIGHT, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }} className="reveal">
            Financing Available
          </span>
          <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', color: headingColor, textTransform: 'uppercase' }} className="reveal">
            A Roof Is an Investment. Pay for It on Your Terms.
          </h2>
          <p style={{ color: bodyColor, fontSize: '1rem', marginBottom: '2rem', marginTop: '0.75rem' }} className="reveal">
           A quality roof is a significant investment, but it does not have to be paid for all at once. We offer flexible financing options that can help you move forward with the roof your home needs while choosing a payment plan that works for your budget.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1.25rem', marginBottom: '2.5rem' }}>
            {[
              { icon: '📅', label: 'Flexible Terms', sub: ' Payment plans up to 60 months' },
              { icon: '0%', label: '0% Interest', sub: 'Available for qualified applicants' },
              { icon: '⚡', label: 'Instant Decision', sub: 'Apply online in minutes' },
              { icon: '🛡️', label: 'No Compromise', sub: 'The same quality materials and workmanship' },
            ].map((item, i) => (
              <div key={item.label} className={`card reveal-scale reveal-delay-${i + 1}`} style={{ textAlign: 'center', border: `1px solid rgba(0,114,198,0.25)` }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{item.icon}</div>
                <div style={{ fontWeight: 800, color: headingColor, fontSize: '1.1rem' }}>{item.label}</div>
                <div style={{ color: softText, fontSize: '0.8rem' }}>{item.sub}</div>
              </div>
            ))}
          </div>
          <Link to={ROUTES.FINANCING} className="btn-primary">Check Your Rate <ArrowRight size={16} /></Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;

